// Sentry Configuration
// Initializes error tracking for the application

import * as Sentry from "@sentry/react";

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const ENVIRONMENT = import.meta.env.NODE_ENV || 'development';
const APP_VERSION = import.meta.env.VITE_APP_VERSION || '1.0.0';

export function initSentry() {
  // Only initialize Sentry if DSN is configured
  if (!SENTRY_DSN) {
    console.warn('Sentry DSN not configured - error tracking disabled');
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: ENVIRONMENT,
    release: `aegis@${APP_VERSION}`,
    
    // Performance monitoring
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Performance traces sample rate (10% in production)
    tracesSampleRate: ENVIRONMENT === 'production' ? 0.1 : 1.0,

    // Session replay sample rate (10% in production, 100% on errors)
    replaysSessionSampleRate: ENVIRONMENT === 'production' ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,

    // Ignore common errors
    ignoreErrors: [
      // Network errors
      'NetworkError',
      'Failed to fetch',
      'Network request failed',
      'Load failed',
      
      // Browser extension errors
      'Extension context invalidated',
      'chrome-extension://',
      'moz-extension://',
      
      // ResizeObserver loop errors (benign)
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      
      // Third-party scripts
      'Script error',
    ],

    // Filter out sensitive data
    beforeSend(event, hint) {
      // Remove sensitive data from breadcrumbs
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => {
          if (breadcrumb.data) {
            // Remove potential passwords, tokens, keys
            const sensitiveKeys = ['password', 'token', 'key', 'secret', 'authorization'];
            Object.keys(breadcrumb.data).forEach((key) => {
              if (sensitiveKeys.some(k => key.toLowerCase().includes(k))) {
                breadcrumb.data![key] = '[REDACTED]';
              }
            });
          }
          return breadcrumb;
        });
      }

      // Remove sensitive request data
      if (event.request) {
        if (event.request.headers) {
          delete event.request.headers['authorization'];
          delete event.request.headers['cookie'];
        }
        if (event.request.data) {
          // Redact sensitive fields from request body
          const data = event.request.data as unknown;
          if (typeof data === 'object' && data !== null) {
            const dataObj = data as Record<string, unknown>;
            const sensitiveFields = ['password', 'confirmPassword', 'token', 'apiKey', 'secret'];
            sensitiveFields.forEach(field => {
              if (dataObj[field]) {
                dataObj[field] = '[REDACTED]';
              }
            });
          }
        }
      }

      return event;
    },

    // Add user context
    beforeBreadcrumb(breadcrumb) {
      // Don't log console.debug in production
      if (ENVIRONMENT === 'production' && breadcrumb.category === 'console' && breadcrumb.level === 'debug') {
        return null;
      }
      return breadcrumb;
    },
  });
}

// Set user context (call this after authentication)
export function setSentryUser(user: { id: string; email?: string; accountType?: string } | null) {
  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      accountType: user.accountType,
    });
  } else {
    Sentry.setUser(null);
  }
}

// Add custom context
export function setSentryContext(context: string, data: Record<string, unknown>) {
  Sentry.setContext(context, data);
}

// Manually capture exception
export function captureException(error: Error, context?: Record<string, unknown>) {
  if (context) {
    Sentry.withScope((scope) => {
      Object.entries(context).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
      Sentry.captureException(error);
    });
  } else {
    Sentry.captureException(error);
  }
}

// Capture message (for non-error events)
export function captureMessage(message: string, level: 'info' | 'warning' | 'error' = 'info') {
  Sentry.captureMessage(message, level);
}

// Add breadcrumb manually
export function addBreadcrumb(breadcrumb: {
  message: string;
  category?: string;
  level?: 'debug' | 'info' | 'warning' | 'error';
  data?: Record<string, unknown>;
}) {
  Sentry.addBreadcrumb(breadcrumb);
}
